import { faChevronDown } from '@fortawesome/pro-light-svg-icons';
import {
  faBars,
  faBook,
  faCog,
  faCommentQuestion,
  faInfoCircle,
  faSignOut,
  faUserAlt,
  faUserCircle,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Link from 'next/link';
import React, { useCallback, useRef, useState } from 'react';
import styled from 'styled-components';

import DropdownMenu from '@components/DropdownMenu';
import DropdownWrapper from '@components/DropdownWrapper';
import { brand, grey300, fontDark, lineLight } from '@constants/colors';
import { isBrowser } from '@constants/live-prep';
import { laptop, tablet } from '@constants/media-queries';
import { navigation } from '@constants/navigation-data';
import { admin, books, login, logout, userAccount } from '@constants/routes';
import { space } from '@constants/spaces';
import Avatar from '@elements/Avatar';
import Button from '@elements/Button';
import TopBar from '@elements/TopBar';
import { Role, useGetAccountInfoQuery } from '@graphql/generated/graphql';
import useClickOutside from '@hooks/useClickOutside';
import useMediaQuery from '@hooks/useMediaQuery';

import MobileNavigation from './mobile/MobileNavigation';

const Container = styled.div`
  border-bottom: 1px solid ${lineLight};
  padding: ${space * 1.5}px ${space * 2}px;
  display: flex;
  justify-content: flex-end;

  ${tablet} {
    padding: ${space * 1.5}px ${space * 3}px;
  }

  ${laptop} {
    padding-left: ${space * 5}px;
    padding-right: ${space * 4}px;
  }
`;

const StyledTopBar = styled(TopBar)`
  border-bottom: 1px solid ${lineLight};
`;

const Component = styled.div`
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  list-style: none;
  flex-grow: 1;
  justify-content: flex-end;

  & > :not(:first-child) {
    margin-left: ${space * 2}px;
  }

  ${tablet} {
    & > :not(:first-child) {
      margin-left: ${space * 3}px;
    }
  }
`;

const PageList = styled.li`
  order: 0;
  position: relative;

  ${laptop} {
    display: flex;
    order: 1;
  }

  & > button {
    margin-right: 16px;
  }
`;

const Account = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  & > :nth-child(2n) {
    margin: 0 8px;
  }
`;

const AccountLinks = styled.li`
  margin-left: auto;
  order: 2;
  & > ul {
    & > li {
      margin-left: 16px;
      ${laptop} {
        margin-left: ${space * 3}px;
      }
      &:first-of-type {
        a {
          &:hover {
            color: ${brand};
          }
        }
      }
    }
  }
`;

const FlexContainer = styled.ul`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  list-style: none;
`;

const SButton = styled.button`
  background: none;
  cursor: pointer;
`;

const StyledAvatar = styled(Avatar)`
  color: ${fontDark};
  font-size: 20px;
  box-shadow: none;
  background-color: ${grey300};

  :hover {
    background-color: ${lineLight};
  }
`;

type Props = {
  children?: React.ReactNode;
};

const EditorHeader = ({ children }: Props) => {
  const [showNav, setShowNav] = useState(false);
  const { data } = useGetAccountInfoQuery();
  const { isTablet } = useMediaQuery();
  const wrapperRef = useRef<HTMLUListElement>(null);
  const isAdmin = data?.account.role === Role.Admin;
  const hasVisibleChilren =
    children && Array.isArray(children)
      ? children.filter((child) => typeof child !== undefined).length > 0
      : children;
  useClickOutside(
    wrapperRef,
    useCallback(() => setShowNav(false), []),
  );

  const openChat = () => {
    if (isBrowser && window.$crisp) {
      window.$crisp.push(['do', 'chat:show']);
      window.$crisp.push(['do', 'helpdesk:search']);
    }
  };

  const toggleShowNav = () => {
    setShowNav(!showNav);
  };

  const accountLinks = [
    {
      label: 'Mijn Account',
      href: data?.account.firstName ? userAccount : login,
    },
    {
      label: 'Mijn Boeken',
      href: data?.account.firstName ? books : login,
    },
  ];

  const accountDropdown = [
    {
      name: 'Mijn boeken',
      to: books,
      icon: faBook,
    },
    {
      name: 'Mijn account',
      href: userAccount,
      icon: faUserAlt,
    },
    {
      name: 'Klantenservice',
      onClick: openChat,
      icon: faInfoCircle,
      show: isTablet,
    },
    {
      name: 'Admin',
      href: admin,
      icon: faCog,
      show: isAdmin,
    },
    {
      name: 'Uitloggen',
      href: logout,
      icon: faSignOut,
    },
  ];

  const accountDropdownComponent = <DropdownMenu items={accountDropdown} />;

  const mobileMenu = (
    <>
      <PageList>
        {isTablet && (
          <Button
            set="tertiary"
            onClick={toggleShowNav}
            aria-label="Menu wisselen"
          >
            <FontAwesomeIcon icon={faBars} />
          </Button>
        )}
        {isTablet && (
          <MobileNavigation
            showNav={showNav}
            toggleShowNav={toggleShowNav}
            navigation={navigation}
            accountLinks={accountLinks}
          />
        )}
      </PageList>
      <AccountLinks>
        <FlexContainer>
          {data?.account.firstName ? (
            <DropdownWrapper dropdownChildren={accountDropdownComponent}>
              <Account>
                <span>{data?.account.firstName}</span>
                <FontAwesomeIcon icon={faChevronDown} />
              </Account>
            </DropdownWrapper>
          ) : (
            <Link href={login} passHref>
              <Account as="a">
                <FontAwesomeIcon icon={faUserCircle} />
                <span>Inloggen</span>
              </Account>
            </Link>
          )}
        </FlexContainer>
      </AccountLinks>
    </>
  );

  const desktopMenu = (
    <Component>
      {children}
      {data?.account.firstName ? (
        <DropdownWrapper dropdownChildren={accountDropdownComponent}>
          <Account>
            <span>{data?.account.firstName}</span>
            <FontAwesomeIcon icon={faChevronDown} />
          </Account>
        </DropdownWrapper>
      ) : (
        <Link href={login} passHref>
          <Account as="a">
            <FontAwesomeIcon icon={faUserCircle} />
            <span>Inloggen</span>
          </Account>
        </Link>
      )}
      <SButton onClick={openChat}>
        <StyledAvatar icon={faCommentQuestion} />
      </SButton>
    </Component>
  );

  return (
    <>
      <StyledTopBar>{isTablet ? mobileMenu : desktopMenu}</StyledTopBar>
      {isTablet && hasVisibleChilren && (
        <Container>
          <Component>{children}</Component>
        </Container>
      )}
    </>
  );
};

export default EditorHeader;
